import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LumetierWordmark from "@/components/LumetierWordmark";

/* Handles the Emergent Google OAuth callback.

   Flow:
   1. Emergent redirects back to /admin/auth/callback#session_id=xxx
   2. We extract the session_id from the URL fragment (synchronously to avoid
      the race-condition where AuthProvider's /auth/me fires first).
   3. POST it to our backend /api/auth/google/exchange.
   4. Backend exchanges with Emergent, matches by email, returns our JWT.
   5. Store JWT, set user, navigate to /admin.

   REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */

export default function AuthCallback() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error("Google sign-in returned no session. Please try again.");
      nav("/admin/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);
    const rememberMe = localStorage.getItem("allure_remember_me") !== "false";

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", {
          session_id: sessionId,
          remember_me: rememberMe,
        });
        setSession(data.token, data.user);
        // Clear the fragment so refreshing the page doesn't re-trigger the exchange
        window.history.replaceState({}, "", "/admin");
        toast.success(`Welcome back, ${data.user.name || data.user.email}`);
        nav("/admin", { replace: true });
      } catch (err) {
        const msg = formatApiErrorDetail(err.response?.data?.detail) || "Google sign-in failed.";
        toast.error(msg);
        nav("/admin/login", { replace: true });
      }
    })();
  }, [nav, setSession]);

  return (
    <div className="min-h-screen bg-[#070709] text-stone-100 flex items-center justify-center px-6" data-testid="auth-callback-page">
      <div className="text-center">
        <LumetierWordmark tone="light" className="text-xl" testid="callback-wordmark" />
        <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-stone-500">
          Signing you in…
        </div>
        <div className="mt-8 inline-block w-6 h-6 border-2 border-stone-700 border-t-[hsl(var(--gold))] rounded-full animate-spin" />
      </div>
    </div>
  );
}
